/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  WRITE: 'write',
  ABNORMAL_FAILED: 'abnormalFailed', // エラー
  FAILED: 'failed', // 共通エラー
};

/**  カメラのボタン設定 */
const CAMERA = {
  ON: 'buttonOn',
  OFF: 'buttonOff'
};

export default {
  name: 'requirement089',
  data() {
    return {
      front: CAMERA.OFF, // フロントカメラのボタン
      left: CAMERA.OFF, // 左サイドカメラのボタン
      right: CAMERA.OFF, // 右サイドカメラのボタン
      rear: CAMERA.OFF, // リヤカメラのボタン
      phase: PHASE.START, // 初期表示の画面フェーズ
      isLoading: false, // スピナー表示
      systemId: '', // システムID
      selectDataRecord: '', // カメラ接続設定画面の選択したカメラのDataRecord
      isCallExit: false, // exit呼び出しフラグ
      modal_requirement_089: false,
      cameraSettingsOptions: [CAMERA.ON, CAMERA.OFF]
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ABNORMAL_FAILED:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    },

    /**モーダルサイズ */
    modalSize() {
      switch (this.phase) {
        case PHASE.WRITE:
          return 'lg';
        default:
          return 'md';
      }
    },

    buttonOnText() {
      return this.$t(`${this.i18nWsKey}.button_label_true`);
    },

    buttonOffText() {
      return this.$t(`${this.i18nWsKey}.button_label_false`);
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_089 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_089 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** NonDefaultSession遷移,データ読出し処理 */
    async read() {
      try {
        this.isLoading = true;
        // NonDefaultSession遷移の処理を行うため、trueに変更
        this.isCallExit = true;
        const token = session.getToken();

        // NonDefaultSession遷移,データ読出し（バックエンドAPI）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.phase = PHASE.WRITE;
              this.getDisplayInfo(response.data.user_data.connection_settings);
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了の場合
              if (response.data.async_code === error.WSP_089_SESSION_ERROR) {
                // NonDefaultSession遷移が異常終了の場合、現在セッションがDefaultSessionのためfalseに変更
                this.isCallExit = false;
              }
              this.phase = PHASE.ABNORMAL_FAILED;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /**
     * データ書込み,DefaultSession遷移処理
     * @param {{ front: Boolean, left: Boolean, right: Boolean, rear: Boolean }} connectionSettings 
     */
    async write(connectionSettings) {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // データ書込み（バックエンドAPI）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write',
            connection_settings: connectionSettings
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了の場合
              this.phase = PHASE.ABNORMAL_FAILED;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** DefaultSession遷移処理 */
    async exit() {
      const token = session.getToken();

      // DefaultSession遷移（バックエンドAPI）
      await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'exit',
        },
        // エラー発生のコールバックを実装
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
      this.isCallExit = false;
    },

    /** Dispose処理 */
    async dispose() {
      const token = session.getToken();

      // Dispose処理（バックエンドAPI）
      await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'dispose',
        },
        // エラー発生のコールバックを実装
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      // 結果にかかわらず、Wizardを閉じる
      this.closeWizard();
    },

    /**
     * カメラ接続設定書き込み画面に表示するボタン配置取得
     * @param {Object} connectionSettings 設定値
     */
    getDisplayInfo(connectionSettings) {
      this.front = connectionSettings.front ? CAMERA.ON : CAMERA.OFF;
      this.left = connectionSettings.left ? CAMERA.ON : CAMERA.OFF;
      this.right = connectionSettings.right ? CAMERA.ON : CAMERA.OFF;
      this.rear = connectionSettings.rear ? CAMERA.ON : CAMERA.OFF;
    },

    /** カメラ接続設定書込み画面の「OK」ボタン押下イベント */
    async writeProcess() {
      const connectionSettings = this.getConnectionSettings();
      await this.write(connectionSettings);
    },

    /** カメラ接続設定書込み画面の「キャンセル」/エラー画面の「OK」/共通エラー画面の「OK」ボタン押下イベント */
    async exitProcess() {
      this.isLoading = true;
      if (this.isCallExit){
        await this.exit();
      }
      await this.dispose();
      this.isLoading = false;
    },

    /**
     * 現在表示されているバリアント書き込みの情報を取得する
     * @returns {{ front: Boolean, left: Boolean, right: Boolean, rear: Boolean }} connectionSettings
     */
    getConnectionSettings() {
      const connectionSettings = {
        front: false,
        left: false,
        right: false,
        rear: false
      };

      if (this.front === CAMERA.ON) {
        connectionSettings.front = true;
      }
      if (this.left === CAMERA.ON) {
        connectionSettings.left = true;
      }
      if (this.right === CAMERA.ON) {
        connectionSettings.right = true;
      }
      if (this.rear === CAMERA.ON) {
        connectionSettings.rear = true;
      }

      return connectionSettings;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        // Wizardを閉じる
        this.closeWizard();
        return;
      }
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
    updateCameraValue(camera, value) {
      switch (camera) {
        case 'front':
          this.front = value;
          break;
        case 'left':
          this.left = value;
          break;
        case 'right':
          this.right = value;
          break;
        case 'rear':
          this.rear = value;
          break;
      }
    }
  },

  async mounted() {
    this.init();
    // NonDefaultSession遷移,データ読出し
    await this.read();
  },
};